import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import {
  CAMERA_DEFAULTS,
  CAMERA_MODES,
  CAMERA_MODE_DESC,
  CAMERA_MODE_LABEL,
  CAMERA_MODE_ORDER,
  type CameraModeKey,
  type CameraModeValues,
} from './cameraModes';
import { useUnits } from '@/hooks/useUnits';
import { Icons, type IconSpec } from 'turbotwin-sdk/icons';
import { Icon } from '../Icon';

// Portal theme tokens (styles.css / orientation-editor.css palette).
const T = {
  bg: 'var(--panel)',
  paper: 'var(--panel-3)',
  primary: 'var(--amber)',
  primaryLight: 'var(--amber-2)',
  primarySoft: 'var(--amber-soft)',
  text: 'var(--text)',
  textSecondary: 'var(--muted)',
  border: 'var(--border)',
};

const POP_WIDTH = 540;

// Camera mode icons from the TurboTwin vocabulary.
const MODE_ICON: Record<CameraModeKey, IconSpec> = {
  freeLook: Icons.view.cameraFreeLook,
  freeOrbit: Icons.view.cameraOrbit,
  walk: Icons.view.cameraWalk,
};

/**
 * The unified camera control, mirroring the Unreal client's camera panel:
 * a single button opens a dark popover with a title, an icon+label mode
 * switcher (label shows only on the selected mode), two columns (the active
 * mode's settings + a live controls cheat-sheet), a per-mode Reset, and a
 * close button. Rendered through a portal (fixed + high z-index) so it escapes
 * the 3D pane's `overflow: hidden` and sits above the viewer iframe.
 */
export function CameraControl({
  mode,
  values,
  onModeChange,
  onValuesChange,
}: {
  mode: CameraModeKey;
  values: CameraModeValues;
  onModeChange: (m: CameraModeKey) => void;
  onValuesChange: (v: CameraModeValues) => void;
}) {
  const { fmtSpeed } = useUnits();
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const left = Math.max(8, Math.min(r.left, window.innerWidth - POP_WIDTH - 8));
    setPos({ top: r.bottom + 6, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onViewport = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onViewport);
    window.addEventListener('scroll', onViewport, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onViewport);
      window.removeEventListener('scroll', onViewport, true);
    };
  }, [open]);

  const cfg = CAMERA_MODES[mode];
  const modeVals = values[mode] as Record<string, number | boolean>;

  const setVal = (key: string, v: number | boolean) =>
    onValuesChange({ ...values, [mode]: { ...values[mode], [key]: v } });
  const resetMode = () =>
    onValuesChange({ ...values, [mode]: { ...CAMERA_DEFAULTS[mode] } });

  return (
    <>
      <button
        ref={btnRef}
        className={'oe-camera-btn' + (open ? ' on' : '')}
        onClick={() => setOpen((o) => !o)}
        title={`Camera Controls · ${CAMERA_MODE_LABEL[mode]}`}
      >
        <Icon of={Icons.view.camera} size={18} />
        <span className="oe-camera-sep" />
        <Icon of={MODE_ICON[mode]} size={18} />
      </button>
      {open &&
        createPortal(
          <div
            ref={popRef}
            role="dialog"
            aria-label="Camera Controls"
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              zIndex: 9999,
              width: POP_WIDTH,
              maxWidth: 'calc(100vw - 16px)',
              padding: '10px 12px',
              borderRadius: 6,
              background: T.bg,
              color: T.text,
              border: `1px solid ${T.border}`,
              boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
              boxSizing: 'border-box',
              font: '13px/1.4 system-ui, sans-serif',
            }}
          >
            {/* Title + close */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontWeight: 700, fontSize: 14 }}>Camera Controls</span>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                style={{
                  marginLeft: 'auto',
                  width: 26,
                  height: 26,
                  border: 'none',
                  borderRadius: 6,
                  background: 'transparent',
                  color: T.textSecondary,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon of={Icons.action.remove} size={16} />
              </button>
            </div>

            {/* Mode switcher: icon always, label only when selected */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
              {CAMERA_MODE_ORDER.map((m) => {
                const on = m === mode;
                return (
                  <button
                    key={m}
                    onClick={() => onModeChange(m)}
                    title={`${CAMERA_MODE_LABEL[m]}: ${CAMERA_MODE_DESC[m]}`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '6px 13px',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      background: on ? T.primarySoft : T.paper,
                      color: on ? T.primary : T.textSecondary,
                      fontSize: 12,
                    }}
                  >
                    <Icon of={MODE_ICON[m]} size={18} />
                    {on && <span>{CAMERA_MODE_LABEL[m]}</span>}
                  </button>
                );
              })}
            </div>

            {/* Body: settings | cheat-sheet */}
            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ flex: 1, minWidth: 190 }}>
                {cfg.controls.map((c) =>
                  c.kind === 'slider' ? (
                    <label key={c.key} style={{ display: 'block', marginBottom: 8 }}>
                      <div style={rowStyle}>
                        <span style={{ color: T.textSecondary }}>{c.label}</span>
                        <span style={{ minWidth: 52, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {c.speed ? fmtSpeed(modeVals[c.key] as number) : (modeVals[c.key] as number).toFixed(2)}
                        </span>
                      </div>
                      <input
                        type="range"
                        min={c.min}
                        max={c.max}
                        step={c.step}
                        value={modeVals[c.key] as number}
                        onChange={(e) => setVal(c.key, Number(e.target.value))}
                        style={{ width: '100%', accentColor: T.primary }}
                      />
                    </label>
                  ) : (
                    <button
                      key={c.key}
                      onClick={() => setVal(c.key, !(modeVals[c.key] as boolean))}
                      title={c.label}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginBottom: 12,
                        padding: 0,
                        border: 'none',
                        background: 'transparent',
                        color: T.text,
                        cursor: 'pointer',
                        font: 'inherit',
                      }}
                    >
                      <span style={{ color: (modeVals[c.key] as boolean) ? T.primary : T.textSecondary, display: 'inline-flex' }}>
                        <Icon of={modeVals[c.key] ? Icons.action.show : Icons.action.hide} />
                      </span>
                      {c.label}
                    </button>
                  ),
                )}
              </div>

              {/* Controls cheat-sheet */}
              <div style={{ width: 300, borderLeft: `1px solid ${T.border}`, paddingLeft: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6 }}>
                  {CAMERA_MODE_LABEL[mode]} Controls
                </div>
                {cfg.cheats.map((row) => (
                  <div key={row.action} style={{ display: 'flex', gap: 10, padding: '3px 0', fontSize: 12 }}>
                    <span style={{ minWidth: 100, color: T.text }}>{row.action}</span>
                    <span style={{ color: T.primaryLight, fontVariantNumeric: 'tabular-nums' }}>{row.keys}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Reset (per active mode) */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <button
                onClick={resetMode}
                style={{
                  padding: '5px 12px',
                  border: 'none',
                  borderRadius: 4,
                  background: T.paper,
                  color: T.text,
                  cursor: 'pointer',
                  fontSize: 12,
                }}
              >
                Reset
              </button>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

const rowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: 12,
  marginBottom: 3,
};
